import {
  AgDataGrid,
  AgDataGridProps,
  PaginationModel,
} from '@australiangreens/ag-internal-components';
import {
  DOMAINS_WITH_IDS,
  EXPANDED_TABLE_PROPERTIES,
  TABLE_LINK_STYLE_FOR_TEMPLATE,
} from '@/constants';
import { SearchTemplate } from '@/types';
import { CollapsibleText } from '@components/CollapsibleText';
import { useUserDetails } from '@hooks/useUserDetails';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import Link from '@mui/material/Link';
import {
  GridCallbackDetails,
  GridCellParams,
  GridColDef,
  GridColumnVisibilityModel,
  GridSortModel,
} from '@mui/x-data-grid';
import moment from 'moment';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router';

type ToolTipMenuItemWrapperProps = {
  title?: string;
  disabled?: boolean;
  dataTestId: string;
  menuLabel: string;
  onClick?: () => void;
};

export const ToolTipMenuItemWrapper = (props: ToolTipMenuItemWrapperProps) => {
  const { title, disabled, dataTestId, menuLabel, onClick } = props;
  if (disabled) {
    return (
      <Tooltip title={title}>
        <span>
          <MenuItem disabled={disabled} data-testid={dataTestId} onClick={onClick}>
            {menuLabel}
          </MenuItem>
        </span>
      </Tooltip>
    );
  } else {
    return (
      <MenuItem disabled={disabled} data-testid={dataTestId} onClick={onClick}>
        {menuLabel}
      </MenuItem>
    );
  }
};

export type ActionsTemplateMenuMode = 'shareable-full' | 'shareable-coordinator-only';

interface ActionsTemplateMenuProps {
  rowId: string;
  organisations: string[];
  handleDelete: () => void;
  handleEdit: () => void;
  handleUpdate: () => void;
  handleCopy: () => void;
  handleCopyLink: () => void;
  disableEllipsis: boolean;
  menuMode: ActionsTemplateMenuMode;
}

export const ActionsTemplateMenu = ({
  rowId,
  handleDelete,
  handleEdit,
  handleUpdate,
  handleCopy,
  handleCopyLink,
  disableEllipsis,
  menuMode,
}: ActionsTemplateMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const closeMenu = () => setAnchorEl(null);

  const copyLabel = 'Copy template';

  return (
    <>
      <IconButton
        id={`${rowId}-open-action-menu-icon`}
        data-testid={`${rowId}-open-action-menu-icon`}
        onClick={(evt) => {
          setAnchorEl(evt.currentTarget);
        }}
        aria-label="Open actions menu"
        aria-haspopup="true"
        aria-controls={`${rowId}-action-menu-icon`}
        size="small"
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id={`${rowId}-action-menu`}
        data-testid={`${rowId}-open-action-menu`}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        anchorEl={anchorEl}
      >
        {menuMode === 'shareable-coordinator-only' ? (
          <ToolTipMenuItemWrapper
            dataTestId={`${rowId}-open-action-menu-copy-link`}
            onClick={() => {
              handleCopyLink();
              closeMenu();
            }}
            menuLabel={'Copy link'}
          />
        ) : (
          [
            <ToolTipMenuItemWrapper
              key="edit"
              title={'You cannot edit a template that is shared across states and territories'}
              disabled={disableEllipsis}
              dataTestId={`${rowId}-open-action-menu-edit`}
              onClick={() => {
                handleEdit();
                closeMenu();
              }}
              menuLabel={'Edit name & description'}
            />,
            <ToolTipMenuItemWrapper
              key="update"
              title={'You cannot edit a template that is shared across states and territories'}
              disabled={disableEllipsis}
              dataTestId={`${rowId}-open-action-menu-update`}
              onClick={() => {
                handleUpdate();
                closeMenu();
              }}
              menuLabel={'Update template'}
            />,
            <ToolTipMenuItemWrapper
              key="copy"
              dataTestId={`${rowId}-open-action-menu-copy`}
              onClick={() => {
                handleCopy();
                closeMenu();
              }}
              menuLabel={copyLabel}
            />,
            menuMode === 'shareable-full' ? (
              <ToolTipMenuItemWrapper
                key="copylink"
                dataTestId={`${rowId}-open-action-menu-copy-link`}
                onClick={() => {
                  handleCopyLink();
                  closeMenu();
                }}
                menuLabel={'Copy link'}
              />
            ) : null,
            <ToolTipMenuItemWrapper
              key="delete"
              title={'You cannot delete a template that is shared across states and territories'}
              disabled={disableEllipsis}
              dataTestId={`${rowId}-open-action-menu-delete`}
              onClick={() => {
                handleDelete();
                closeMenu();
              }}
              menuLabel={'Delete'}
            />,
          ]
        )}
      </Menu>
    </>
  );
};

type SavedSearchTemplatesTableProps = {
  data: SearchTemplate[];
  isLoading: boolean;
  paginationModel: PaginationModel;
  onPaginationModelChange?: (model: PaginationModel) => void;
  sortModel?: GridSortModel | undefined;
  columnVisibilityModel?: GridColumnVisibilityModel;
  onColumnVisibilityModelChange?: (
    model: GridColumnVisibilityModel,
    details: GridCallbackDetails
  ) => void;
  onSortModelChange?: ((model: GridSortModel, details: GridCallbackDetails) => void) | undefined;
  activateDelete: (searchTemplate: SearchTemplate) => void;
  activateEdit: (searchTemplate: SearchTemplate) => void;
  activateUpdate: (searchTemplate: SearchTemplate) => void;
  activateCopy: (searchTemplate: SearchTemplate) => void;
  activateCopyLink: (searchTemplate: SearchTemplate) => void;
} & Partial<AgDataGridProps>;

const SearchTemplatesTable = ({
  data,
  isLoading,
  paginationModel,
  onPaginationModelChange = undefined,
  rowCount = undefined,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  sortModel = undefined,
  onSortModelChange = undefined,
  activateDelete,
  activateEdit,
  activateUpdate,
  activateCopy,
  activateCopyLink,
}: SavedSearchTemplatesTableProps) => {
  const { data: userDetails } = useUserDetails();

  const isPrincipalCoordinator = userDetails?.userLevel === 'Principal Coordinator';
  const isCoordinatorOnly = userDetails?.userLevel === 'Coordinator';

  const showEllipsisColumn = isPrincipalCoordinator || isCoordinatorOnly;

  const actionsMenuMode: ActionsTemplateMenuMode = isPrincipalCoordinator
    ? 'shareable-full'
    : 'shareable-coordinator-only';

  const preFilteredDomainsIds = DOMAINS_WITH_IDS.filter(
    (x) => userDetails?.domainToUserLevels[x.id] === 'Principal Coordinator'
  ).map((x) => x.id as string);

  function isEllipsisItemsDisabled(organisations: string[]) {
    if (preFilteredDomainsIds.includes('ag') || preFilteredDomainsIds.includes('fedmps')) {
      return false;
    }
    for (const organisation of organisations) {
      if (!preFilteredDomainsIds.includes(organisation)) {
        return true;
      }
    }
    return false;
  }

  const COORDINATOR_COLUMN_DEFS: GridColDef<SearchTemplate, string, string>[] = showEllipsisColumn
    ? [
        {
          headerName: '',
          field: 'Actions',
          align: 'right',
          flex: 1.5,
          type: 'actions',
          headerClassName: 'lastcolumnSeparator',
          minWidth: 60,
          sortable: false,
          renderCell: ({ row }) => (
            <ActionsTemplateMenu
              rowId={String(row.id)}
              organisations={row.organisations}
              handleDelete={() => activateDelete(row)}
              handleEdit={() => activateEdit(row)}
              handleUpdate={() => activateUpdate(row)}
              handleCopy={() => activateCopy(row)}
              handleCopyLink={() => activateCopyLink(row)}
              disableEllipsis={isEllipsisItemsDisabled(row.organisations)}
              menuMode={actionsMenuMode}
            />
          ),
        },
      ]
    : ([] as GridColDef<SearchTemplate, string, string>[]);

  const COLUMN_DEFS: GridColDef<SearchTemplate, string, string>[] = [
    {
      headerName: 'Template name',
      field: 'name',
      align: 'left',
      flex: 350,
      editable: false,
      renderCell: (params) => (
        <Link
          component={RouterLink}
          style={TABLE_LINK_STYLE_FOR_TEMPLATE}
          to={`/template/${params.row.id}/load`}
          replace
          state={{ preloadedTemplate: params.row }}
        >
          {params.row.name}
        </Link>
      ),
    },
    {
      headerName: 'Date created',
      field: 'createdAt',
      align: 'left',
      flex: 120,
      editable: false,
      type: 'date',
      valueFormatter: (value) =>
        value != null && value !== '' ? moment(value).format('DD/MM/YYYY') : '',
    },
    {
      headerName: 'Created by',
      field: 'createdBySub',
      align: 'left',
      flex: 150,
      editable: false,
    },
    {
      headerName: 'Description',
      field: 'description',
      align: 'left',
      renderCell: (params) => <CollapsibleText text={params.row.description} />,
      flex: 492,
      editable: false,
    },
    {
      headerName: 'Id',
      headerAlign: 'left',
      field: 'id',
      align: 'left',
      flex: 100,
      editable: false,
      type: 'number',
      valueFormatter: (value: unknown) =>
        value == null || value === '' || (typeof value === 'number' && Number.isNaN(value))
          ? ''
          : String(value),
    },
    ...COORDINATOR_COLUMN_DEFS,
  ];

  return (
    <AgDataGrid
      loading={isLoading}
      rows={data}
      columns={COLUMN_DEFS}
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      noResultsMessage={'No searches template meet your filter criteria'}
      noRowsMessage={'No templates meet your filter criteria'}
      skeletonColumnWidths={COLUMN_DEFS.map((column) =>
        columnVisibilityModel && columnVisibilityModel[column.field] === false
          ? 0
          : (column.flex ?? 0)
      )}
      rowCount={rowCount}
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={onColumnVisibilityModelChange}
      pageSizeOptions={[5, 10, 25, 50, 100]}
      disableRowSelectionOnClick={true}
      disableColumnFilter={true}
      getCellClassName={(params: GridCellParams) => {
        if (params.field === 'description') {
          return 'collapsible';
        }
        return '';
      }}
      {...EXPANDED_TABLE_PROPERTIES}
    />
  );
};

export default SearchTemplatesTable;
